/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { ArrowRight, Loader2, Disc3, AlertTriangle, Ban, Shield, HelpCircle, X, Zap } from "lucide-react";
import CameraLiveness from "./CameraLiveness";
import { api, fmtErr, getHealth } from "../lib/api";

// ---- Cloudflare Turnstile (script + global) ----
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    if (document.getElementById("cf-turnstile-script")) {
      const waitForIt = setInterval(() => {
        if (window.turnstile) { clearInterval(waitForIt); resolve(window.turnstile); }
      }, 100);
      return;
    }
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(s);
  });
}

// HMAC-SHA256 via WebCrypto, returns hex string
async function hmacSha256Hex(keyStr, message) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey("raw", enc.encode(keyStr), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
  const arr = new Uint8Array(sig);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function canonicalLivenessJson({ nonce, frame_count, face_hit_rate, motion_score, bbox_movement, measured_at }) {
  // MUST match server's canonical encoding (sort_keys=True, no spaces)
  const obj = {
    bbox_movement: Math.round(bbox_movement * 10000) / 10000,
    face_hit_rate: Math.round(face_hit_rate * 10000) / 10000,
    frame_count,
    measured_at,
    motion_score: Math.round(motion_score * 10000) / 10000,
    nonce,
  };
  return JSON.stringify(obj);
}

export default function GuestDoor({ roomId, onAdmitted, initialBanned = false }) {
  const [search] = useSearchParams();
  const debug = search.get("debug") === "1";
  const [step, setStep] = useState(initialBanned ? "banned" : "form"); // form | liveness | submitting | success | banned | error
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [siteKey, setSiteKey] = useState(null);
  const [tsToken, setTsToken] = useState(null);
  const [precheckData, setPrecheckData] = useState(null); // { precheck_token, nonce, thresholds }
  const [attempts, setAttempts] = useState(0);
  const [showTips, setShowTips] = useState(false);
  const [emailAuthEnabled, setEmailAuthEnabled] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const widgetIdRef = useRef(null);
  const tsContainerRef = useRef(null);

  useEffect(() => {
    api.get("/turnstile/config")
      .then((r) => setSiteKey(r.data.site_key))
      .catch(() => setSiteKey(null));
    getHealth().then((h) => setEmailAuthEnabled(!!h.email_auth_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (step !== "form" || !siteKey || !tsContainerRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const ts = await loadTurnstile();
        if (cancelled) return;
        if (widgetIdRef.current != null) {
          ts.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
        widgetIdRef.current = ts.render(tsContainerRef.current, {
          sitekey: siteKey,
          theme: "dark",
          callback: (t) => setTsToken(t),
          "error-callback": () => setTsToken(null),
          "expired-callback": () => setTsToken(null),
        });
      } catch (e) { console.warn("Turnstile load failed", e); }
    })();
    return () => { cancelled = true; };
  }, [step, siteKey]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!displayName.trim() || displayName.trim().length < 3) return setError("Display name must be at least 3 characters.");
    if (!tsToken) return setError("Please complete the human check.");
    if (!consentAccepted) return setError("Please accept the Terms and Privacy Policy to continue.");
    // R14.4-C — Persist the chosen display name so PreRoomPreview pre-fills it
    // instead of asking again. The user reported having to type their name
    // twice; now there's exactly ONE name prompt in the entry flow (GuestDoor),
    // and Pre-Room Preview reads it from the same localStorage key.
    try {
      const k = "sr-preview-devices";
      const existing = JSON.parse(localStorage.getItem(k) || "{}");
      localStorage.setItem(k, JSON.stringify({ ...existing, displayName: displayName.trim() }));
    } catch { /* ignore */ }

    try {
      const fp = await (await FingerprintJS.load()).get();
      const r = await api.post(`/rooms/${roomId}/guest/precheck`, {
        display_name: displayName.trim(),
        device_fp: fp.visitorId,
        turnstile_token: tsToken,
      });
      // Phase 4 — liveness step removed from the door. Admit immediately after
      // precheck succeeds; the cam permission is requested by Room.jsx when the
      // participant publishes their video.
      setStep("submitting");
      const adm = await api.post(`/rooms/${roomId}/guest/liveness`, {
        precheck_token: r.data.precheck_token,
      });
      setStep("success");
      onAdmitted?.(adm.data);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 403 && data?.error === "BANNED") {
        setStep("banned");
        return;
      }
      if (err?.response?.status === 410) {
        setError("This room has ended.");
        return;
      }
      setError(fmtErr(data?.detail || data, "Could not verify. Please try again."));
      // Reset turnstile
      try { window.turnstile?.reset(widgetIdRef.current); } catch { /* ignore */ }
      setTsToken(null);
    }
  };

  // Phase 4 — facial-liveness handlers retained as no-ops (legacy compat). The
  // door now admits straight after Turnstile + precheck. The cam permission is
  // requested by Room.jsx on entry, not here.
  const handleLivenessPass = async () => {};
  const handleLivenessFail = () => {};

  const retryLiveness = () => {
    if (attempts >= 2) { setStep("contact-host"); return; }
    setAttempts((a) => a + 1);
    setError("");
    setStep("liveness");
  };

  if (step === "banned") {
    return (
      <Centered icon={<Ban className="w-7 h-7 text-[#FF007F]" />} title="You've been removed from this room"
        message="The host must reset the room for you to return." testid="guest-banned" />
    );
  }

  if (step === "contact-host") {
    return (
      <Centered icon={<AlertTriangle className="w-7 h-7 text-amber-400" />} title="Liveness check failed too many times"
        message="Please contact the host of this room." testid="guest-contact-host" />
    );
  }

  return (
    <div className="min-h-screen sr-aurora grid place-items-center px-4 py-10" data-testid="guest-door">
      <div className="w-full max-w-md sr-glass rounded-2xl p-6 sm:p-8 sr-fade-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center">
            <Disc3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Join as guest</div>
            <div className="font-display font-bold">Room {roomId}</div>
          </div>
        </div>

        {step === "form" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h1 className="font-display text-2xl font-black tracking-tight">Step inside</h1>
            <p className="text-sm text-white/60 -mt-2">No account needed. Just confirm you're a human.</p>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-1.5">Display name</label>
              <input
                type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. selena_nyc" minLength={3} maxLength={64} required
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF]/60 focus:ring-2 focus:ring-[#00F0FF]/20"
                data-testid="guest-name-input"
              />
            </div>
            {siteKey ? (
              <div className="flex justify-center" ref={tsContainerRef} data-testid="turnstile-container" />
            ) : (
              <div className="text-xs text-amber-300/80 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" /> Loading human check…
              </div>
            )}
            {error && <p className="text-sm text-[#FF007F] font-medium" data-testid="guest-error">{error}</p>}
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="guest-consent-row">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF007F] cursor-pointer shrink-0"
                data-testid="guest-consent-checkbox"
              />
              <span className="text-xs text-white/70 leading-snug">
                I am 18+ and I accept Full Spectrum Center's{" "}
                <Link to="/legal/terms" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="guest-terms-link">Terms</Link>
                {" "}and{" "}
                <Link to="/legal/privacy" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="guest-privacy-link">Privacy Policy</Link>.
              </span>
            </label>
            <button
              type="submit" disabled={!tsToken || displayName.trim().length < 3 || !consentAccepted}
              className="w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg px-6 py-3.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between"
              data-testid="guest-continue-btn"
            >
              <span>Continue</span><ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {step === "liveness" && precheckData && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-bold">Smile for the camera</h2>
            <p className="text-sm text-white/60">We just need to confirm you're a real human, on camera, right now.</p>
            <CameraLiveness
              thresholds={precheckData}
              onPass={handleLivenessPass}
              onFail={handleLivenessFail}
              debug={debug}
            />
            <button onClick={() => setShowTips(true)} className="text-xs text-[#00F0FF]/80 hover:text-[#00F0FF] flex items-center gap-1.5 mx-auto" data-testid="guest-tips-link">
              <HelpCircle className="w-3.5 h-3.5" /> Liveness keeps failing? Click here for tips
            </button>
          </div>
        )}

        {step === "submitting" && (
          <div className="flex items-center gap-2 text-white/70 justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#00F0FF]" />
            <span>Letting you in…</span>
          </div>
        )}

        {step === "liveness-failed" && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-bold text-[#FF007F]">Couldn't verify a live person</h2>
            <p className="text-sm text-white/60">{error}</p>
            <button
              onClick={retryLiveness}
              className="w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg px-6 py-3 hover:opacity-90"
              data-testid="guest-retry-liveness-btn"
            >
              Try again
            </button>
            <button onClick={() => setShowTips(true)} className="text-xs text-[#00F0FF]/80 hover:text-[#00F0FF] flex items-center gap-1.5 mx-auto" data-testid="guest-tips-link-failed">
              <HelpCircle className="w-3.5 h-3.5" /> Tips for making it pass
            </button>
            <p className="text-xs text-white/40 text-center">Attempt {attempts + 1} of 3</p>
          </div>
        )}
      </div>

      {/* Tips modal */}
      {showTips && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur px-4" data-testid="guest-tips-modal">
          <div className="max-w-md w-full sr-glass rounded-2xl p-5 border border-white/10">
            <div className="flex items-start gap-2 mb-3">
              <HelpCircle className="w-5 h-5 text-[#00F0FF] mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold">Liveness tips</h3>
                <p className="text-xs text-white/50 mt-0.5">Most failures are lighting or framing.</p>
              </div>
              <button onClick={() => setShowTips(false)} className="text-white/40 hover:text-white" data-testid="guest-tips-close"><X className="w-4 h-4" /></button>
            </div>
            <ul className="text-sm text-white/80 list-disc pl-5 space-y-1.5 mb-4">
              <li>Make sure your face is well-lit and clearly visible (no strong backlight).</li>
              <li>Move your head a little — a small nod or side-to-side is enough.</li>
              <li>Turn off any virtual backgrounds, AR filters, or beauty modes.</li>
              <li>Use your laptop's built-in camera instead of a virtual one (OBS Virtual Cam, etc).</li>
              <li>If you've tried 3 times, ask the host to invite you directly via Discord.</li>
            </ul>
            <p className="text-xs text-white/40 mb-3">
              Want to see what the camera is measuring in real time? Append <span className="font-mono text-[#00F0FF]">?debug=1</span> to the room URL.
            </p>
            {emailAuthEnabled && (
              <button
                onClick={() => { setShowTips(false); handleDevSkip(); }}
                className="w-full bg-amber-500/15 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 font-bold rounded-lg px-4 py-2.5 text-sm flex items-center justify-center gap-2"
                data-testid="guest-dev-skip-btn"
                title="Only available when ENABLE_EMAIL_AUTH=true on the backend">
                <Zap className="w-4 h-4" /> Skip liveness (dev only)
              </button>
            )}
            <button onClick={() => setShowTips(false)} className="w-full mt-2 text-xs text-white/60 hover:text-white py-2">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ icon, title, message, testid }) {
  return (
    <div className="min-h-screen sr-aurora grid place-items-center px-6" data-testid={testid}>
      <div className="sr-glass rounded-2xl p-8 max-w-md text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white/5 border border-white/10 grid place-items-center">{icon}</div>
        <h2 className="font-display text-2xl font-bold mb-2">{title}</h2>
        <p className="text-white/60 text-sm">{message}</p>
      </div>
    </div>
  );
}
